import {
    IFacetGroupFilter,
    IFacetSearchResult,
    IPaginationViewModel,
} from 'Types/Litium';
import CloseIcon from 'Icons/closeThin.svg?react';
import getFiltrationValuesAndUpdateUrlQuery from 'Utils/getFiltrationValuesAndUpdateUrlQuery';
import { useIsOverflow } from 'Scripts/Hooks/useIsOverflow';
import { useRef } from 'react';

type IProductFilterProps = {
    facetSearchResult: IFacetSearchResult;
    pagination: IPaginationViewModel;
    totalProductsCountTitle: string;
    getFiltration: (
        param: {
            name: string;
            value: string;
        }[]
    ) => void;
};

const ProductFilterTags = ({
    facetSearchResult,
    pagination,
    totalProductsCountTitle,
    getFiltration,
}: IProductFilterProps) => {
    const wrapperRef = useRef();
    const taglistRef = useRef();

    const { refXOverflowing, refXScrollBegin, refXScrollEnd } = useIsOverflow(
        taglistRef,
        wrapperRef,
        facetSearchResult
    );

    const handleClearAllFilters = () => {
        const filtrationValues = getFiltrationValuesAndUpdateUrlQuery(
            facetSearchResult.facetFilters,
            new FormData()
        );
        getFiltration(filtrationValues);
    };

    const handleRemoveFilterValue = (
        filter: IFacetGroupFilter,
        selectedOptions: string
    ) => {
        const value = filter.options.find(item => item.id === selectedOptions);
        const filtrationValues = getFiltrationValuesAndUpdateUrlQuery(
            facetSearchResult.facetFilters,
            new FormData(),
            { name: filter.id, value: value.id }
        );

        getFiltration(filtrationValues);
    };

    return (
        <div className="relative mb-3 mt-3 hidden w-full justify-between gap-3 overflow-hidden lg:mt-5 lg:flex">
            <span
                className={`relative block h-5 whitespace-nowrap font-roboto text-sm font-normal text-black before:absolute before:-right-3 before:-top-0.5 before:h-6 before:w-2.5 before:bg-merino before:transition-shadow before:duration-layout ${
                    refXOverflowing && !refXScrollBegin
                        ? 'before:shadow-[3px_0px_2px_-2px_rgba(0,_0,_0,_1)]'
                        : 'before:shadow-none'
                }`}
            >
                {`${pagination?.totalCount} ${totalProductsCountTitle}`}
            </span>
            <div ref={wrapperRef} className="w-full overflow-x-auto">
                <ul ref={taglistRef} className="flex h-10 gap-4 before:ml-auto">
                    {facetSearchResult.facetFilters?.map(filter =>
                        filter.selectedOptions.map(
                            (selectedOption, index) =>
                                selectedOption && (
                                    <li key={index}>
                                        <button
                                            onClick={() =>
                                                handleRemoveFilterValue(
                                                    filter,
                                                    selectedOption
                                                )
                                            }
                                            type="button"
                                            className="flex items-center gap-6 bg-beautyBush stroke-1 px-4 text-sm font-normal"
                                        >
                                            <span className="sr-only">
                                                {
                                                    facetSearchResult
                                                        .translations
                                                        .filterRemove
                                                }
                                            </span>
                                            <span className="whitespace-nowrap capitalize">
                                                {filter.label}:{' '}
                                                {filter.options
                                                    .find(
                                                        option =>
                                                            option.id ===
                                                            selectedOption
                                                    )
                                                    ?.label?.toLowerCase()}
                                            </span>
                                            <CloseIcon className="h-3 w-3 stroke-2" />
                                        </button>
                                    </li>
                                )
                        )
                    )}
                </ul>
            </div>
            {facetSearchResult.facetFilters?.find(
                filter => filter.selectedOptions.length > 0
            ) && (
                <button
                    onClick={handleClearAllFilters}
                    type="button"
                    className={`relative flex h-5 cursor-pointer items-center gap-6 whitespace-nowrap stroke-1 pl-2 text-sm font-normal underline after:absolute after:-left-3 after:-top-0.5 after:h-6 after:w-2.5 after:bg-merino after:transition-shadow after:duration-layout ${
                        refXOverflowing && !refXScrollEnd
                            ? 'after:shadow-[-3px_0px_2px_-2px_rgba(0,_0,_0,_1)]'
                            : 'after:shadow-none'
                    }`}
                >
                    <span>
                        {facetSearchResult.translations.filterRemoveAll}
                    </span>
                    <CloseIcon className="h-3 w-3 stroke-2" />
                </button>
            )}
        </div>
    );
};

export default ProductFilterTags;
